import api from './api';
import { ServiceOrder, ServiceOrderStatus, PaymentMethod } from '@/types';

export interface ServiceOrderItemCreate {
  product_id?: number;
  custom_name?: string;
  quantity?: number;
  unit_price?: number;
  ml_quantity?: number;  // Para productos sell_by_ml
  notes?: string;
}

export interface ServiceOrderCreate {
  patient_id?: number;
  owner_id?: number;
  medical_record_id?: number;
  notes?: string;
  items?: ServiceOrderItemCreate[];
}

export interface ServiceOrderFilters {
  status?: ServiceOrderStatus;
  patient_id?: number;
  owner_id?: number;
  skip?: number;
  limit?: number;
}

export const serviceOrdersService = {
  async getOrders(filters?: ServiceOrderFilters): Promise<ServiceOrder[]> {
    const response = await api.get<ServiceOrder[]>('/service-orders', { params: filters });
    return response.data;
  },

  async getPendingOrders(): Promise<ServiceOrder[]> {
    const response = await api.get<ServiceOrder[]>('/service-orders/pending');
    return response.data;
  },

  async getOpenOrders(): Promise<ServiceOrder[]> {
    const response = await api.get<ServiceOrder[]>('/service-orders/open');
    return response.data;
  },

  async getOrder(id: number): Promise<ServiceOrder> {
    const response = await api.get<ServiceOrder>(`/service-orders/${id}`);
    return response.data;
  },

  async createOrder(data: ServiceOrderCreate): Promise<ServiceOrder> {
    const response = await api.post<ServiceOrder>('/service-orders', data);
    return response.data;
  },

  async addItem(orderId: number, item: ServiceOrderItemCreate): Promise<ServiceOrder> {
    const response = await api.post<ServiceOrder>(`/service-orders/${orderId}/items`, item);
    return response.data;
  },

  async updateItem(
    orderId: number,
    itemId: number,
    data: { quantity?: number; unit_price?: number; notes?: string }
  ): Promise<ServiceOrder> {
    const response = await api.put<ServiceOrder>(`/service-orders/${orderId}/items/${itemId}`, null, {
      params: data,
    });
    return response.data;
  },

  async removeItem(orderId: number, itemId: number): Promise<ServiceOrder> {
    const response = await api.delete<ServiceOrder>(`/service-orders/${orderId}/items/${itemId}`);
    return response.data;
  },

  async markReady(orderId: number): Promise<ServiceOrder> {
    const response = await api.post<ServiceOrder>(`/service-orders/${orderId}/ready`);
    return response.data;
  },

  async chargeOrder(
    orderId: number,
    paymentMethod: PaymentMethod = 'cash',
    paymentReference?: string,
    discountPercentage?: number,
    discountAmount?: number,
    chargeAsCash?: boolean,
  ): Promise<ServiceOrder> {
    const response = await api.post<ServiceOrder>(`/service-orders/${orderId}/charge`, null, {
      params: {
        payment_method: paymentMethod,
        payment_reference: paymentReference,
        discount_percentage: discountPercentage || undefined,
        discount_amount: discountAmount || undefined,
        // El backend recalcula el precio de la comanda segun el medio de pago, asi que
        // esto tiene que viajar: sin el, la comanda se cobraria igual con recargo.
        charge_as_cash: chargeAsCash || undefined,
      },
    });
    return response.data;
  },

  async cancelOrder(orderId: number): Promise<ServiceOrder> {
    const response = await api.post<ServiceOrder>(`/service-orders/${orderId}/cancel`);
    return response.data;
  },

  async deleteOrder(orderId: number): Promise<void> {
    await api.delete(`/service-orders/${orderId}`);
  },
};

// Labels para UI
export const ORDER_STATUS_LABELS: Record<ServiceOrderStatus, string> = {
  open: 'Abierta',
  ready_to_charge: 'Lista para Cobrar',
  charged: 'Cobrada',
  cancelled: 'Cancelada',
};

export const ORDER_STATUS_COLORS: Record<ServiceOrderStatus, string> = {
  open: 'bg-blue-100 text-blue-800',
  ready_to_charge: 'bg-yellow-100 text-yellow-800',
  charged: 'bg-green-100 text-green-800',
  cancelled: 'bg-red-100 text-red-800',
};

export const PAYMENT_METHOD_LABELS: Record<PaymentMethod, string> = {
  cash: 'Efectivo',
  card: 'Tarjeta',
  transfer: 'Transferencia',
  other: 'Otro',
};
